<script setup>
import { slugifyString } from '~/utils/slugify'

const props = defineProps({
  to: {
    type: String,
  },
  image: {
    type: Object,
    required: true,
  },
  content: {
    type: Object,
  },
  flipped: {
    type: Boolean,
    default: false,
  },
  button: {
    type: Object,
  },
})
</script>

<template>
  <div class="row">
    <div
      class="col-12 col-800-9 offset-800-1"
      :class="{ 'offset-800-2': props.flipped }"
    >
      <component
        :is="to ? 'NuxtLink' : 'div'"
        :to="props.to"
        class="d-flex flex-column flex-800-row image-text"
        :class="{
          'image-text--flipped': props.flipped,
        }"
      >
        <div class="image-text__image">
          <NuxtImg
            v-if="props.image?.src"
            sizes="sm:340px md:440px lg:800px"
            densities="1x 2x"
            :src="props.image?.src"
            :alt="props.image?.alt"
            loading="lazy"
            format="webp"
            :placeholder="[300, 200, 60, 2]"
          />
        </div>
        <div class="image-text__content">
          <h2
            v-if="props.content?.title"
            :id="slugifyString(props.content?.title)"
            class="scale-3 scale-3--bold-uppercase"
          >
            {{ props.content?.title }}
          </h2>
          <slot>
            <div
              class="equal-base image-text__text"
              v-html="props.content?.text"
            />
          </slot>
          <FormButton
            v-if="props.button?.to"
            class="l-button--outlined image-text__button"
            :label="props.button?.label"
            :to="props.button?.to"
            :rel="props.button?.rel"
            :component="props.to ? 'span' : 'NuxtLink'"
          />
        </div>
      </component>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/scss/variables.scss';
@import 'bootstrap/scss/mixins';

.image-text {
  min-height: clamp(240px, 11.11vw + 204.44px, 400px);
  transition: 0.5s ease;
  text-decoration: none;

  &--flipped {
    @include media-breakpoint-up(800) {
      flex-direction: row-reverse !important;

      .image-text {
        &__image {
          width: 56%;
          position: relative;
          overflow: hidden;
          margin-right: unset;
          margin-left: 30px;

          img {
            transition: 0.5s ease;
            object-fit: cover;
            width: 100%;
            height: 100%;
          }
        }

        &__content {
          margin: auto 30px auto 0;
        }
      }
    }
  }

  &:hover,
  &:focus,
  &:active {
    .image-text__image {
      img {
        transform: scale(1.05);
        transition: 0.5s ease;
      }
    }
  }

  &__image {
    width: 56%;
    position: relative;
    overflow: hidden;
    margin-right: 30px;

    height: 60vh;
    min-height: 340px;
    max-height: clamp(340px, 25vw + 260px, 700px);

    @include media-breakpoint-down(800) {
      width: auto;
      margin-right: 0;
    }

    @include media-breakpoint-down(640) {
      margin: 0 $padding-container-negative;
    }

    img {
      transition: 0.5s ease;
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  &__content {
    width: 44%;
    margin: auto 0 auto 30px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @include media-breakpoint-down(800) {
      width: 100%;
      margin: 45px 0 0 0;
    }
  }

  &__header {
    margin-bottom: 5px;
  }

  &__button {
    margin-top: 15px;
  }
}
</style>
